import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
} from '@mui/material';
import {
  HelpOutline as HelpIcon,
  CheckCircleOutline as CheckIcon,
} from '@mui/icons-material';

interface HelpButtonProps {
  /** Dialog title (already localized). */
  title: string;
  /** Optional intro paragraph (already localized). */
  intro?: string;
  /** Bulleted guidance items (already localized). */
  items: string[];
}

/**
 * Reusable "?" help button: shows a concise, on-demand guide dialog.
 * Place it next to a page title and pass localized strings.
 */
const HelpButton: React.FC<HelpButtonProps> = ({ title, intro, items }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip title={t('help.tooltip')}>
        <IconButton size="small" color="primary" onClick={() => setOpen(true)} aria-label={t('help.tooltip')}>
          <HelpIcon />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#667eea', fontWeight: 700 }}>
          <HelpIcon fontSize="small" /> {title}
        </DialogTitle>
        <DialogContent>
          {intro && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {intro}
            </Typography>
          )}
          <List dense>
            {items.map((item, i) => (
              <ListItem key={i} alignItems="flex-start" sx={{ py: 0.3 }}>
                <ListItemIcon sx={{ minWidth: 30, mt: 0.5 }}>
                  <CheckIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary={<Box component="span" sx={{ fontSize: '0.9rem' }}>{item}</Box>} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HelpButton;
